import {
    useCallback,
    useState, useEffect,
} from 'react'
import {
    useMutation,
} from '@tanstack/react-query'
import isEqual from 'lodash/isEqual'
import {
    useJWTToken,
} from 'app/Auth'

import useHandleStatusRequest from 'app/hooks/useHandleStatusRequest'
import {
    PropsTabs,
} from 'app/Apps/OrderManagement/Orders/orders.types'

import {
    OrderModificationType,
} from 'app/enums/label.enums'
import request from 'app/requests/Order/Order.requests'
import simpleApi from 'app/services/simpleApi'
import {
    ModificationData,
    ModificationCustomerOrder,
} from '../dataUpdate.types'

const useModifyOrder = ({
    data,
    onSuccess,
}: PropsTabs): ModificationData => {
    const token = useJWTToken()
    const {
        onSuccess: onSuccessModification,
        onError,
    } = useHandleStatusRequest({
        id: 'OrderByNumberView',
        title: 'Order Modified',
        onSuccess,
    })

    const modifyCustomerOrderData = useCallback(() => {
        return {
            requiresManualBillingReviewOps: data?.billing?.requiresManualBillingReviewOps,
            commentForBillingOps: data?.commentForBillingOps,
            personInCharge: data?.personInCharge,
            orderReceived: data?.orderReceived,
            customerReference: data?.customerReference,
            createdBy: data?.createdBy,
            orderNumber: data?.orderNumber,
            temperatureRange: data?.temperatureRange,
            freightForwarder: data?.freightForwarder,
            leaseStartTimestamp: data?.billing?.leaseStartTimestamp,
            longTermLease: data?.longTermLease,
        }
    }, [data])

    const [
        value,
        setValue,
    ] = useState<ModificationCustomerOrder>(modifyCustomerOrderData())

    useEffect(() => {
        if (!isEqual(data?.orderNumber, value?.orderNumber)) {
            setValue(modifyCustomerOrderData())
        }
    }, [
        data?.orderNumber,
        modifyCustomerOrderData,
        value?.orderNumber,
    ])

    const {
        mutate,
        isPending: isLoading,
    } = useMutation({
        mutationFn: (modificationType: OrderModificationType) => {
            return simpleApi({
                ...request.updateData({
                    orderNumber: data?.orderNumber,
                    modificationType,
                    data: {
                        customerReference: value.customerReference,
                        orderReceived: value.orderReceived,
                        leaseStartTimestamp: value.leaseStartTimestamp,
                        requiresManualBillingReviewOps: value.requiresManualBillingReviewOps,
                        commentForBillingOps: value.commentForBillingOps,
                        freightForwarder: value?.freightForwarder,
                        longTermLease: value?.longTermLease,
                    },
                }),
                token,
            })
        },
        onSuccess: onSuccessModification,
        onError,
    })

    return {
        value,
        setValue,
        updateOrder: mutate,
        isSaveDisabled: isEqual(modifyCustomerOrderData(), value),
        isLoading,
    }
}

export default useModifyOrder
